import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { useState, useEffect } from 'react';
import { useAuthStore } from '@/store/authStore';
import {
  fetchNotificationSettings,
  updateNotificationSettings,
  type NotificationSettingsForm,
} from '@/services/settings';
import { useTranslation } from 'react-i18next';

export function NotificationSettings() {
  const { user } = useAuthStore();
  const [isLoading, setIsLoading] = useState(false);
  const [isManager, setIsManager] = useState(false);
  const { t } = useTranslation();

  const [notifications, setNotifications] = useState<NotificationSettingsForm>({
    email: {
      orders: true,
      marketing: false,
      updates: true,
    },
    push: {
      orders: true,
      marketing: false,
      updates: false,
    },
  });

  // Check if user is a manager or higher
  useEffect(() => {
    if (user?.role) {
      setIsManager(['manager', 'admin', 'super_admin'].includes(user.role));
    }
  }, [user?.role]);

  // Load notification settings from database
  useEffect(() => {
    const loadNotificationSettings = async () => {
      if (!user?.id) return;
      setIsLoading(true);

      try {
        const settings = await fetchNotificationSettings(user.id);

        if (settings) {
          setNotifications({
            email: {
              orders: settings.email_orders,
              marketing: settings.email_marketing,
              updates: settings.email_updates,
            },
            push: {
              orders: settings.push_orders,
              marketing: settings.push_marketing,
              updates: settings.push_updates,
            },
          });
        }
      } catch (error) {
        console.error('Error loading notification settings:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadNotificationSettings();
  }, [user?.id]);

  const handleToggle = (
    category: 'email' | 'push',
    type: 'orders' | 'marketing' | 'updates'
  ) => {
    setNotifications({
      ...notifications,
      [category]: {
        ...notifications[category],
        [type]: !notifications[category][type],
      },
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.id || !isManager) return;

    setIsLoading(true);

    try {
      await updateNotificationSettings(user.id, notifications);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTestFcmNotification = async () => {
   
  };

  // If user isn't a manager, show a message
  if (!isManager) {
    return (
      <Card className='mt-6'>
        <CardHeader>
          <CardTitle>{t('emailNotifications')}</CardTitle>
          <CardDescription>{t('managerPermissionsRequired')}</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className='grid gap-6'>
        <Card>
          <CardHeader>
            <CardTitle>{t('emailNotifications')}</CardTitle>
            <CardDescription>{t('manageEmailNotifications')}</CardDescription>
          </CardHeader>
          <CardContent className='space-y-4'>
            <div className='flex items-center justify-between'>
              <Label htmlFor='email-orders' className='flex flex-col gap-1'>
                <span>{t('orderUpdates')}</span>
                <span className='font-normal text-xs text-muted-foreground'>
                  {t('receiveOrderUpdates')}
                </span>
              </Label>
              <Switch
                id='email-orders'
                checked={notifications.email.orders}
                onCheckedChange={() => handleToggle('email', 'orders')}
                disabled={isLoading}
              />
            </div>

            <div className='flex items-center justify-between'>
              <Label htmlFor='email-marketing' className='flex flex-col gap-1'>
                <span>{t('marketing')}</span>
                <span className='font-normal text-xs text-muted-foreground'>
                  {t('receiveMarketingUpdates')}
                </span>
              </Label>
              <Switch
                id='email-marketing'
                checked={notifications.email.marketing}
                onCheckedChange={() => handleToggle('email', 'marketing')}
                disabled={isLoading}
              />
            </div>

            <div className='flex items-center justify-between'>
              <Label htmlFor='email-updates' className='flex flex-col gap-1'>
                <span>{t('productUpdates')}</span>
                <span className='font-normal text-xs text-muted-foreground'>
                  {t('receiveProductUpdates')}
                </span>
              </Label>
              <Switch
                id='email-updates'
                checked={notifications.email.updates}
                onCheckedChange={() => handleToggle('email', 'updates')}
                disabled={isLoading}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('pushNotifications')}</CardTitle>
            <CardDescription>{t('managePushNotifications')}</CardDescription>
          </CardHeader>
          <CardContent className='space-y-4'>
            <div className='flex items-center justify-between'>
              <Label htmlFor='push-orders' className='flex flex-col gap-1'>
                <span>{t('orderUpdates')}</span>
                <span className='font-normal text-xs text-muted-foreground'>
                  {t('receiveOrderUpdates')}
                </span>
              </Label>
              <Switch
                id='push-orders'
                checked={notifications.push.orders}
                onCheckedChange={() => handleToggle('push', 'orders')}
                disabled={isLoading}
              />
            </div>
            {/* FCM Test Button */}
           {process.env.NODE_ENV === 'development' && <div className='flex items-center justify-between'>
              <Label className='flex flex-col gap-1'>
                <span>Test Firebase FCM Notification</span>
                <span className='font-normal text-xs text-muted-foreground'>
                  Send a test notification to your browser using Firebase FCM
                </span>
              </Label>
              <Button type='button' onClick={handleTestFcmNotification} disabled={isLoading}>
                Send Test
              </Button>
            </div>
}
            <div className='flex items-center justify-between'>
              <Label htmlFor='push-marketing' className='flex flex-col gap-1'>
                <span>{t('marketing')}</span>
                <span className='font-normal text-xs text-muted-foreground'>
                  {t('receiveMarketingUpdates')}
                </span>
              </Label>
              <Switch
                id='push-marketing'
                checked={notifications.push.marketing}
                onCheckedChange={() => handleToggle('push', 'marketing')}
                disabled={isLoading}
              />
            </div>

            <div className='flex items-center justify-between'>
              <Label htmlFor='push-updates' className='flex flex-col gap-1'>
                <span>{t('productUpdates')}</span>
                <span className='font-normal text-xs text-muted-foreground'>
                  {t('receiveProductUpdates')}
                </span>
              </Label>
              <Switch
                id='push-updates'
                checked={notifications.push.updates}
                onCheckedChange={() => handleToggle('push', 'updates')}
                disabled={isLoading}
              />
            </div>
          </CardContent>
        </Card>

        <div className='flex justify-end'>
          <Button
            type='submit'
            className='w-full sm:w-auto'
            disabled={isLoading}>
            {isLoading ? t('savingPreferences') : t('savePreferences')}
          </Button>
        </div>
      </div>
    </form>
  );
}
